import React, { useState, useRef, useEffect } from "react";
import { CircularProgress, Container } from "@mui/material";
import { Card, CardHeader, Icon } from "@ui5/webcomponents-react";
import { MarkerArea } from "markerjs2";
import ReactMarkdown from 'react-markdown';
import AppLayout from "../../components/AppLayout/AppLayout";
import ButtonNative from "../../components/StyledComponents/ButtonNative";
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import { saveAs } from "file-saver";
import axios from 'axios'; 

const steps = ["Upload Document", "Preview & Annotate", "Extract Text"];

const DocumentExtraction = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [imageSrc, setImageSrc] = useState(null);
  const [isConverting, setIsConverting] = useState(false);
  const [extractedText, setExtractedText] = useState("");
  const [isExtracting, setIsExtracting] = useState(false); // New state for extraction loading
  const imageRef = useRef(null);
  const markerRef = useRef(null);

  useEffect(() => {
    if (activeStep === 0) {
      setImageSrc(null);
      setExtractedText("");
      setUploadedFile(null);
    } else if (activeStep === 1) {
      setExtractedText("");
    }
  }, [activeStep]);

  useEffect(() => {
    if (activeStep === 1) {
      markerRef.current = null;
    }
  }, [uploadedFile, activeStep]);

  const handleFileUpload = async (file) => {
    setUploadedFile(file);
    setImageSrc(null);
    setExtractedText("");

    if (file.type === "application/pdf") {
      await convertPdfToImage(file);
    } else {
      const imageUrl = URL.createObjectURL(file);
      setImageSrc(imageUrl);
    }
  };

  const convertPdfToImage = async (pdfFile) => {
    const formData = new FormData();
    formData.append("uploadedFile", pdfFile);
    setIsConverting(true);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/convert/pdf_to_image`,
        { method: "POST", body: formData }
      );

      if (!response.ok) throw new Error("Failed to convert PDF to image");

      const blob = await response.blob();
      const imageUrl = URL.createObjectURL(blob);
      setImageSrc(imageUrl);
    } catch (error) {
      console.error("Error converting PDF to image:", error);
    } finally {
      setIsConverting(false);
    }
  };

  useEffect(() => {
    if (activeStep === 1 && imageSrc && !isConverting) {
      const img = new Image();
      img.onload = () => startAnnotation();
      img.src = imageSrc;
    }
  }, [activeStep, imageSrc, isConverting]);

  const startAnnotation = () => {
    if (!imageRef.current || markerRef.current) return;

    const markerArea = new MarkerArea(imageRef.current);
    markerRef.current = markerArea;

    markerArea.settings.displayMode = "popup";
    markerArea.uiStyleSettings.zoomButtonVisible = true;
    markerArea.uiStyleSettings.zoomOutButtonVisible = true;
    markerArea.uiStyleSettings.clearButtonVisible = true;
    markerArea.uiStyleSettings.canvasBackgroundColor = "#354a5f";
    markerArea.renderAtNaturalSize = true;
    markerArea.renderImageType = 'image/jpeg';
    markerArea.renderImageQuality = 0.5;
    markerArea.availableMarkerTypes = ['FrameMarker', 'EllipseFrameMarker', 'FreehandMarker'];

    markerArea.addEventListener("render", (event) => {
      const base64AnnotatedImage = event.dataUrl.split(",")[1];
      setImageSrc(`data:image/jpeg;base64,${base64AnnotatedImage}`);
    });

    markerArea.show();
  };

    const extractTextWithGPT = async () => {
    setIsExtracting(true); // Start loading
    const prompt = "OCR extract text only from annotated area(s) and return your response explicitly in Markdown Format";
    const base64Image = imageSrc.split(",")[1];

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000/api';
      const payload = {
        contents: [{
          parts: [
            { "text": prompt },
            { "inline_data": { "mime_type": "image/jpeg", "data": base64Image } }
          ]
        }]
      };
      
      const response = await axios.post(`${backendUrl}/proxy/gemini/generate-content`, payload);

      if (response.data.candidates && response.data.candidates.length > 0) {
        const markdownContent = response.data.candidates[0].content.parts[0].text.replace(/^```markdown\s*|\s*```$/g, '').trim();
        setExtractedText(markdownContent);
        setActiveStep(2);
        markerRef.current = null;
      }

    } catch (error) {
      console.error("Error during extraction:", error);
    } finally {
      setIsExtracting(false); // Stop loading
    }
  };

  const handleNext = async () => {
    if (activeStep === 1 && imageSrc) {
      await extractTextWithGPT();
    } else if (activeStep === steps.length - 1) {
      setActiveStep(0);
      setUploadedFile(null);
      setImageSrc(null);
      setExtractedText("");
      markerRef.current = null;
    } else {
      setActiveStep((prev) => Math.min(prev + 1, steps.length - 1));
    }
  };

  // Export functions
  const handleExport = (format) => {
    let content = extractedText;
    let filename = `extracted_text.${format}`;
    let mimeType = "text/plain";

    if (format === "md") {
      filename = "extracted_text.md";
    } else if (format === "txt") {
      // Convert markdown to plain text by removing markdown syntax
      content = extractedText
        .replace(/[#*_-]+/g, '') // Remove headers, bold, italic, etc.
        .replace(/\n\s*\n/g, '\n') // Remove extra newlines
        .trim();
      filename = "extracted_text.txt";
    }

    const blob = new Blob([content], { type: `${mimeType};charset=utf-8` });
    saveAs(blob, filename);
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <div style={{ padding: "20px", textAlign: "center" }}>
            <ButtonNative component="label" sx={{ padding: "10px 20px" }} disabled={isConverting}>
              {isConverting ? <CircularProgress size={24} /> : "Upload Document"}
              <input
                type="file"
                onChange={(e) => handleFileUpload(e.target.files[0])}
                hidden
                accept="image/*,application/pdf"
              />
            </ButtonNative>
          </div>
        );
      case 1:
        return (
          <div style={{ padding: "20px", display: "flex", justifyContent: "center" }}>
            {isConverting ? (
              <CircularProgress />
            ) : (
              <div style={{
                maxHeight: "500px",
                overflowY: "auto",
                border: "1px solid #ddd",
                backgroundColor: "#f9f9f9",
                padding: "10px"
              }}>
                <img
                  ref={imageRef}
                  src={imageSrc}
                  alt="Document Preview"
                  style={{ maxWidth: "100%", height: "auto" }}
                />
              </div>
            )}
          </div>
        );
      case 2:
        return (
          <div style={{
            padding: "20px",
            maxHeight: "500px",
            overflowY: "auto",
            backgroundColor: "#f9f9f9",
            border: "1px solid #ddd"
          }}>
            <ReactMarkdown>{extractedText}</ReactMarkdown>
            <div style={{ marginTop: "20px", display: "flex", gap: "10px", justifyContent: "center" }}>
              <ButtonNative
                onClick={() => handleExport("txt")}
                sx={{ padding: "10px 20px" }}
                disabled={!extractedText}
              >
                Export as TXT
              </ButtonNative>
              <ButtonNative
                onClick={() => handleExport("md")}
                sx={{ padding: "10px 20px" }}
                disabled={!extractedText}
              >
                Export as MD
              </ButtonNative>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <AppLayout>
      <Container maxWidth="xl" sx={{ textAlign: "center", padding: "40px" }}>
        <Card
          sx={{ padding: "40px" }}
          header={
            <CardHeader
              avatar={<Icon name="document" />}
              subtitleText="Upload, Annotate, and Extract Text from Documents"
              titleText="Document Extractor"
            />
          }
        >
          <Stepper activeStep={activeStep} alternativeLabel sx={{ marginBottom: "20px" }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          <div style={{ marginBottom: "20px", display: "flex", justifyContent: "center", gap: "20px" }}>
            {activeStep !== steps.length - 1 && (
              <ButtonNative
                onClick={() => setActiveStep((prev) => Math.max(prev - 1, 0))}
                disabled={activeStep === 0 || isConverting || isExtracting}
                sx={{ padding: "10px 20px" }}
              >
                Back
              </ButtonNative>
            )}
            <ButtonNative
              onClick={handleNext}
              disabled={(activeStep === 0 && !uploadedFile) || 
                       (activeStep === 1 && (!imageSrc || isExtracting)) || 
                       (activeStep === 2 && !extractedText) || 
                       isConverting}
              sx={{ padding: "10px 20px" }}
            >
              {isExtracting && activeStep === 1 ? (
                <CircularProgress size={24} />
              ) : (
                activeStep === steps.length - 1 ? "Finish" : "Next"
              )}
            </ButtonNative>
          </div>

          {renderStepContent()}
        </Card>
      </Container>
    </AppLayout>
  );
};

export default DocumentExtraction;